import {
  Bell,
  BookOpen,
  BookText,
  Building,
  Calendar,
  CalendarCog,
  CalendarPlus,
  ClipboardCheck,
  ClipboardPlus,
  Cpu,
  Keyboard,
  LayoutDashboard,
  LayoutList,
  MessageSquare,
  PanelRight,
  PhoneCall,
  Search,
  Settings,
  Sparkles,
  Stethoscope,
  Sun,
  Users,
} from "lucide-react";
import type { CommandCategory, CommandDefinition } from "./types";

export const commands: CommandDefinition[] = [
  // --- Navigation ---
  {
    id: "nav.dashboard",
    label: "Go to Dashboard",
    icon: LayoutDashboard,
    category: "navigation",
    defaultHotkey: null,
    action: { type: "navigate", to: { to: "/dashboard" } },
  },
  {
    id: "nav.patients",
    label: "Go to Patients",
    icon: Users,
    category: "navigation",
    defaultHotkey: null,
    action: { type: "navigate", to: { to: "/patients" } },
  },
  {
    id: "nav.schedule",
    label: "Go to Schedule",
    icon: Calendar,
    category: "navigation",
    defaultHotkey: null,
    action: { type: "navigate", to: { to: "/schedule" } },
  },
  {
    id: "nav.tasks",
    label: "Go to Tasks",
    icon: ClipboardCheck,
    category: "navigation",
    defaultHotkey: null,
    action: { type: "navigate", to: { to: "/tasks" } },
  },
  {
    id: "nav.catalog",
    label: "Go to Catalog",
    icon: BookText,
    category: "navigation",
    defaultHotkey: null,
    action: { type: "navigate", to: { to: "/catalog" } },
  },
  {
    id: "nav.resources",
    label: "Go to Locations & Devices",
    icon: LayoutList,
    category: "navigation",
    defaultHotkey: null,
    action: { type: "navigate", to: { to: "/catalog/locations" } },
  },
  {
    id: "nav.settings",
    label: "Go to Settings",
    icon: Settings,
    category: "navigation",
    defaultHotkey: null,
    action: { type: "navigate", to: { to: "/settings" } },
  },
  {
    id: "nav.hotkeys",
    label: "Go to Keyboard Shortcuts",
    icon: Keyboard,
    category: "navigation",
    defaultHotkey: null,
    action: { type: "navigate", to: { to: "/settings/hotkeys" } },
  },
  {
    id: "nav.chat",
    label: "Open Full-Page Chat",
    icon: MessageSquare,
    category: "navigation",
    defaultHotkey: null,
    action: { type: "navigate", to: { to: "/chat" } },
    keywords: ["assistant", "ai", "conversation", "full page"],
  },

  // --- Actions ---
  {
    id: "action.register-patient",
    label: "Register patient",
    icon: Users,
    category: "action",
    defaultHotkey: null,
    action: { type: "effect", run: (ctx) => ctx.openRegisterPatientDialog() },
    keywords: ["new", "patient", "registration", "admission"],
  },
  {
    id: "meta.command-palette",
    label: "Open Command Palette",
    icon: Search,
    category: "action",
    defaultHotkey: "Mod+Shift+P",
    action: { type: "palette-view", view: "home" },
    keywords: ["command", "palette"],
    hidden: true,
  },
  {
    id: "action.search-patients",
    label: "Search Patients",
    icon: Search,
    category: "action",
    defaultHotkey: "Mod+K",
    action: { type: "palette-view", view: "patient-search" },
    keywords: ["find", "lookup"],
  },
  {
    id: "action.call-colleague",
    label: "Call a Colleague",
    icon: PhoneCall,
    category: "action",
    defaultHotkey: null,
    action: { type: "palette-view", view: "colleague-search" },
    keywords: ["call", "ring", "phone", "colleague", "clinician", "video"],
  },
  {
    id: "action.search-chat-history",
    label: "Search Chat History",
    icon: Sparkles,
    category: "action",
    defaultHotkey: "Mod+Shift+K",
    action: { type: "palette-view", view: "chat-history" },
    keywords: ["assistant", "ai", "conversation", "thread"],
  },
  {
    id: "action.manage-schedules",
    label: "Manage Schedules",
    icon: CalendarCog,
    category: "action",
    defaultHotkey: null,
    action: { type: "navigate", to: { to: "/schedule/manage" } },
  },
  {
    id: "action.create-schedule",
    label: "Create Schedule",
    icon: CalendarPlus,
    category: "action",
    defaultHotkey: null,
    action: { type: "navigate", to: { to: "/schedule/manage/new" } },
  },
  {
    id: "action.create-location",
    label: "Create Location",
    icon: Building,
    category: "action",
    defaultHotkey: null,
    action: { type: "navigate", to: { to: "/catalog/locations/new" } },
  },
  {
    id: "action.create-device",
    label: "Create Device",
    icon: Cpu,
    category: "action",
    defaultHotkey: null,
    action: { type: "navigate", to: { to: "/catalog/devices/new" } },
  },
  {
    id: "action.create-questionnaire",
    label: "Create Questionnaire",
    icon: BookOpen,
    category: "action",
    defaultHotkey: null,
    action: { type: "navigate", to: { to: "/catalog/questionnaires/new" } },
  },
  {
    id: "action.create-task",
    label: "Create New Task",
    icon: ClipboardPlus,
    category: "action",
    defaultHotkey: null,
    action: { type: "effect", run: (ctx) => ctx.openCreateTaskDialog() },
    keywords: ["task", "new", "todo"],
  },
  {
    id: "action.add-condition",
    label: "Add Patient Condition",
    icon: Stethoscope,
    category: "action",
    defaultHotkey: null,
    action: { type: "effect", run: (ctx) => ctx.openAddConditionDialog() },
    keywords: ["condition", "diagnosis", "problem list", "icd"],
  },

  // --- Panels ---
  {
    id: "panel.session",
    label: "Toggle Session Panel",
    icon: PanelRight,
    category: "panel",
    defaultHotkey: null,
    action: { type: "effect", run: (ctx) => ctx.toggleSessionPanel() },
  },
  {
    id: "panel.agent",
    label: "Toggle Assistant",
    icon: Sparkles,
    category: "panel",
    defaultHotkey: "Mod+I",
    action: { type: "effect", run: (ctx) => ctx.toggleAgent() },
  },
  {
    id: "panel.agent.switch",
    label: "Switch Assistant Surface",
    icon: MessageSquare,
    category: "panel",
    defaultHotkey: "Mod+Shift+I",
    action: { type: "effect", run: (ctx) => ctx.switchAgentMode() },
    keywords: ["floating", "panel", "swap", "toggle surface"],
  },
  {
    id: "panel.notifications",
    label: "Open Notifications",
    icon: Bell,
    category: "panel",
    defaultHotkey: null,
    action: { type: "effect", run: (ctx) => ctx.openNotifications() },
  },

  // --- Appearance ---
  {
    id: "appearance.toggle-theme",
    label: "Toggle Theme",
    icon: Sun,
    category: "appearance",
    defaultHotkey: null,
    action: { type: "effect", run: (ctx) => ctx.toggleTheme() },
  },
];

export const commandMap: Map<string, CommandDefinition> = new Map(commands.map((c) => [c.id, c]));

export const categoryLabels: Record<CommandCategory, string> = {
  navigation: "Navigation",
  action: "Actions",
  panel: "Panels",
  appearance: "Appearance",
};

/** Visible commands grouped by category, typed against `CommandCategory` — no index-signature cast needed at call sites. */
export const groupedCommands: Record<CommandCategory, CommandDefinition[]> = {
  navigation: [],
  action: [],
  panel: [],
  appearance: [],
};
for (const command of commands) {
  if (command.hidden) continue;
  groupedCommands[command.category].push(command);
}

/** All commands (including hidden ones) grouped by category — for surfaces like the hotkeys settings page that must keep hidden commands rebindable. */
export const allGroupedCommands: Record<CommandCategory, CommandDefinition[]> = {
  navigation: [],
  action: [],
  panel: [],
  appearance: [],
};
for (const command of commands) {
  allGroupedCommands[command.category].push(command);
}
